// Индикатор активного пункта меню, который переезжает между пунктами (подчёркивание в шапке, «пилюля» внизу).
// Позиция — от ref пунктов относительно контейнера, пересчёт при смене пункта и при ResizeObserver.
// Двигаем только transform; первый показ — без перехода, чтобы индикатор не выезжал из угла.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const defaultMeasure = (node, containerRect) => {
  const rect = node.getBoundingClientRect();
  return { x: rect.left - containerRect.left, y: rect.top - containerRect.top, width: rect.width };
};

export function useSlidingIndicator(activeKey, measure = defaultMeasure) {
  // Контейнер — через ref-колбэк: меню может появиться позже (после входа) или смениться
  const [container, setContainer] = useState(null);
  const items = useRef(new Map());
  // { key, x, y, width, hidden }: активного пункта нет в меню (например, «Настройки») — индикатор гаснет
  // на последнем месте и оттуда же поедет, когда пункт снова появится
  const [position, setPosition] = useState(null);
  const [animate, setAnimate] = useState(false);

  const update = useCallback(() => {
    const node = items.current.get(activeKey);
    if (container && node) {
      setPosition({ key: activeKey, hidden: false, ...measure(node, container.getBoundingClientRect()) });
    } else {
      setPosition((prev) => (prev ? { ...prev, hidden: true } : null));
    }
  }, [container, activeKey, measure]);

  useLayoutEffect(update, [update]);

  useEffect(() => {
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => update());
    observer.observe(container);
    items.current.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [container, update]);

  // Переходы включаем после первого кадра с известной позицией
  useEffect(() => {
    if (!position || animate) return undefined;
    const frame = requestAnimationFrame(() => setAnimate(true));
    return () => cancelAnimationFrame(frame);
  }, [position, animate]);

  const itemRef = useCallback((key) => (node) => {
    if (node) items.current.set(key, node);
    else items.current.delete(key);
  }, []);

  return { containerRef: setContainer, itemRef, position, animate };
}
